import bowser from 'bowser';
import classNames from 'classnames';
import type { ImageModalRef } from 'components/organisms/image-modal/ImageModal.data';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScrollStatus } from 'smooth-scrollbar/interfaces';
import { CountryContextProvider } from '../../context/countryContext';
import { HistoryContextProvider } from '../../context/historyContext';
import useEventListener from '../../hooks/useEventListener';
import useFullScreenChange from '../../hooks/useFullScreenChange';
import { isDesktop, useIsDesktopDevice } from '../../hooks/useIsDesktopDevice';
import useIsomorphicLayoutEffect from '../../hooks/useIsomorphicLayoutEffect';
import usePageTracking from '../../hooks/usePageTracking';
import useSitePreloadReleaseFlow from '../../hooks/useSitePreloadReleaseFlow';
import { useTimeout } from '../../hooks/useTimeout';
import styles from '../../pages/_app.module.scss';
import { useGlobalStore } from '../../stores/global';
import { findTransitionController } from '../../util/core-transition-component';
import type { FlowProviderContextType } from '../../util/react-transition-component/components/FlowProvider';
import { useFlowProviderContext } from '../../util/react-transition-component/components/FlowProvider';
import { isForcedPreview } from '../../util/storyblok/api';
import { StorySlug } from '../../util/storyblok/types/api.types';
import type { GlobalSettings } from '../../util/storyblok/types/global.types';
import { stripQueriesFromUrl } from '../../util/url.utils';
import PageWipe from '../atoms/page-wipe/PageWipe';
import type { LocationBlokData } from '../blocks/location/Location.data';
import LocaleList from '../layout/locale-selector/locale-list/LocaleList';
import PreviewModeBar from '../layout/preview-mode-bar/PreviewModeBar';
import SiteFooter from '../layout/site-footer/SiteFooter';
import SiteHeader from '../layout/site-header/SiteHeader';
import type { SiteHeaderRef } from '../layout/site-header/SiteHeader.data';
import MenuOverlay from '../layout/site-header/menu-overlay/MenuOverlay';
import SitePreloader from '../layout/site-preloader/SitePreloader';
import type { SitePreloaderRef } from '../layout/site-preloader/SitePreloader.data';
import RotateScreenModal from '../molecules/rotate-screen-modal/RotateScreenModal';
import VideoModal from '../molecules/video-modal/VideoModal';
import ImageModal from '../organisms/image-modal/ImageModal';
import { DeviceOrientation } from './DeviceOrientation';
import DocumentHead from './DocumentHead';
import { ScrollProvider } from './ScrollProvider';
import { SiteContext } from './SiteContext';
import { SiteHeaderContext } from './SiteHeaderContext';
import { StoryblokEditor } from './StoryblokEditor';
import { ThemeColorProvider } from './ThemeColorProvider';

type SiteProps = {
  globalSettings: GlobalSettings;
  locationsList?: Array<LocationBlokData>;
  domain?: string;
  children: ReactNode;
  // links: GlobalLinks;

  seoTitle?: string;
  seoDescription?: string;
  seoImage?: string;
  seoVideo?: string;
  seoImagePortrait?: string;
  excludeFooter?: boolean;
  noFollow?: boolean;
};

const Site: NextPage<SiteProps> = ({
  locationsList,
  globalSettings,
  domain,
  children,
  seoTitle = globalSettings.seo_title,
  seoDescription = globalSettings.seo_description,
  seoImage = globalSettings.seo_image?.filename,
  seoImagePortrait = globalSettings.seo_image?.filename,
  seoVideo = globalSettings?.seo_video?.filename,
  noFollow = false,
  excludeFooter = false,
}) => {
  const isDesktopDevice = useIsDesktopDevice();
  usePageTracking();
  const isFullScreen = useFullScreenChange();
  const [isVideoModelOpen, setIsVideoModalOpen] = useState<boolean>(false);

  const onVideoModelOpen = useCallback(() => {
    setIsVideoModalOpen(true);
  }, []);

  const onVideoModelClose = useCallback(() => {
    setIsVideoModalOpen(false);
  }, []);

  const store = useGlobalStore();
  store.setLocationsListData(locationsList);
  store.setGlobalSettings(globalSettings);
  store.setHostName(domain);
  const { flowHijacked } = useFlowProviderContext() as FlowProviderContextType;
  const siteHeaderRef = useRef<SiteHeaderRef>(null);
  const router = useRouter();
  const [isScrollLocked, setIsScrollLocked] = useState(false);
  const body = useRef(typeof window !== 'undefined' ? document : null);
  const enableSmoothScroll = useRef(bowser.android || isDesktop);
  const enableGsapScrollSmooth = useRef(bowser.android);
  const sitePreloaderRef = useRef<SitePreloaderRef>(null);
  const imageModalRef = useRef<ImageModalRef>(null);
  const [lockWhenLocaleListIsOpen, setLockWhenLocaleListIsOpen] = useState(false);
  const [renderLocaleList, setRenderLocaleList] = useState(false);

  // Trigger site-wide scrollTrigger.refresh() on OrientationChange
  const { start: refreshScrollTriggerTimeout } = useTimeout(
    () => ScrollTrigger.refresh(),
    100,
    false,
  );
  const handleOrientationChange = useCallback(
    () => refreshScrollTriggerTimeout(),
    [refreshScrollTriggerTimeout],
  );
  useEventListener('orientationchange', handleOrientationChange);

  // Locked the body-scroll on certain states.
  useEffect(() => {
    setIsScrollLocked(isFullScreen || isVideoModelOpen || lockWhenLocaleListIsOpen);
  }, [isFullScreen, isVideoModelOpen, lockWhenLocaleListIsOpen]);

  // Add classes to the body on startup.
  useIsomorphicLayoutEffect(() => {
    if (enableSmoothScroll.current) {
      (body?.current as HTMLDocument).documentElement.classList.add('has-smooth-scroll');
    }
    if (bowser.ios) {
      (body?.current as HTMLDocument).documentElement.classList.add('ios');
    }
    if (bowser.safari) {
      (body?.current as HTMLDocument).documentElement.classList.add('safari');
    }
    if (bowser.chrome) {
      (body?.current as HTMLDocument).documentElement.classList.add('chrome');
    }
  }, [body]);

  // Wait for the preloader to be complete before showing the header.
  useIsomorphicLayoutEffect(() => {
    store.setRenderLocaleListCallback((isOpen) => {
      setRenderLocaleList(true);

      if (enableSmoothScroll.current) {
        setLockWhenLocaleListIsOpen(isOpen);
      }
    });

    (async () => {
      await flowHijacked.current;
      findTransitionController(siteHeaderRef?.current?.elementRef)?.transitionIn();
    })();
  }, []);

  const onScrollUpdate = useCallback(
    (status: ScrollStatus): void => {
      siteHeaderRef?.current?.setPageScrollOffsetY(status?.offset?.y);
    },
    [siteHeaderRef],
  );

  const path = stripQueriesFromUrl(router.asPath);

  const {
    removePreloader,
    isPreloaded,
    onPreloadComplete,
    onPreloaderTransitionOutComplete,
    onPreloaderTransitionOutStart,
  } = useSitePreloadReleaseFlow();

  return (
    <>
      <StoryblokEditor>
        <HistoryContextProvider>
          <CountryContextProvider>
            <DocumentHead
              seoTitle={seoTitle}
              seoDescription={seoDescription}
              seoVideo={seoVideo}
              seoImage={seoImage}
              seoImagePortrait={seoImagePortrait}
              noFollow={noFollow}
            />

            {renderLocaleList && <LocaleList />}

            <MenuOverlay />
            <SiteHeader ref={siteHeaderRef} />

            <DeviceOrientation>
              {!removePreloader && (
                <SitePreloader
                  ref={sitePreloaderRef}
                  preloadComplete={onPreloadComplete}
                  transitionOutStart={onPreloaderTransitionOutStart}
                  transitionOutComplete={onPreloaderTransitionOutComplete}
                />
              )}

              {isPreloaded && (
                <ScrollProvider
                  key={path}
                  wrapperClassName={classNames({
                    [styles.casesScrollBar]: router.asPath.includes(`/${StorySlug.Cases}/`),
                  })}
                  onScrollUpdate={onScrollUpdate}
                  lockScroll={isScrollLocked}
                  enableSmoothScroll={enableSmoothScroll.current}
                  enableGsapScrollSmooth={enableGsapScrollSmooth.current}
                >
                  <ThemeColorProvider isDarkTheme useDocumentElement>
                    <SiteContext.Provider
                      value={{
                        imageModalRef,
                      }}
                    >
                      <SiteHeaderContext.Provider
                        value={{ siteHeaderElementRef: siteHeaderRef?.current?.elementRef }}
                      >
                        {children}
                      </SiteHeaderContext.Provider>
                    </SiteContext.Provider>
                  </ThemeColorProvider>
                  {!excludeFooter && <SiteFooter />}
                </ScrollProvider>
              )}

              {/* For now we are only using the video-modal on tablet/mobile, so let's not construct this if not used */}
              {!isDesktopDevice && (
                <VideoModal onOpen={onVideoModelOpen} onClose={onVideoModelClose} />
              )}

              <ImageModal ref={imageModalRef} />

              <RotateScreenModal />
              <PageWipe />
              {(router.isPreview || isForcedPreview) && <PreviewModeBar />}
            </DeviceOrientation>
          </CountryContextProvider>
        </HistoryContextProvider>
      </StoryblokEditor>
    </>
  );
};

export default Site;
